import DOMPurify from 'dompurify';

// Single source of truth for sanitizing HR-authored document-template HTML (produced by
// docx-preview in the wizard — see utils/docxToHtml.ts). The same config is used everywhere
// the template HTML is injected into the DOM: the wizard preview pane and the client-side PDF
// render (utils/htmlToPdf.ts). Keeping one config means the bytes HR previews are exactly the
// bytes that get screenshotted into the PDF.
//
// docx-preview reproduces Word's look with a scoped <style> block plus class names and inline
// `style`/table attributes, so those must survive sanitization — otherwise the document renders
// unstyled (the very problem this whole change fixes). DOMPurify still strips <script> and
// on*-handlers; base64 `data:` image URIs are allowed on <img> by default, so docx-preview's
// inlined images pass through.
const CONFIG: DOMPurify.Config = {
  // FORCE_BODY is REQUIRED, not optional. docx-preview emits a LEADING <style> block, and
  // the HTML parser treats <style> as head content — so without FORCE_BODY the parser hoists
  // it into <head>, which DOMPurify (body-only by default) then drops, silently stripping ALL
  // of docx-preview's class-based styling (colors, fonts, sizes, table borders). FORCE_BODY
  // parses the fragment as <body> content so the <style> survives. Verified against the real
  // output: with it the 800+ byte <style> block is kept; without it, zero bytes.
  FORCE_BODY: true,
  // Scope the allowlist to HTML + SVG only. Word letterheads render shapes as <svg>, so SVG must
  // stay, but docx-preview never emits MathML — DOMPurify's default allowlist would needlessly keep
  // MathML (and SVG filters), so we pin the profiles to drop that surface. ADD_TAGS/ADD_ATTR below
  // still augment the profile, so the <style> block and docx-preview styling are unaffected.
  // DOMPurify still removes <script>, on*-handlers and javascript: URIs.
  USE_PROFILES: { html: true, svg: true },
  // Allow the <style> block docx-preview emits.
  ADD_TAGS: ['style'],
  // `class` is allowed by DOMPurify by default; listed for clarity. The rest are the
  // presentational attributes docx-preview emits on tables/cells/paragraphs.
  ADD_ATTR: ['style', 'class', 'dir', 'align', 'valign', 'colspan', 'rowspan', 'width', 'height'],
};

export function sanitizeDocumentHtml(html: string): string {
  return DOMPurify.sanitize(html || '', CONFIG) as unknown as string;
}
